import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, EmptyState, Icon, Logo, Modal, Skeleton, Toast } from '@vault/ui';
import type { BrowserName } from '@vault/ui';
import type { Item, Sweep } from '@vault/shared';
import { ApiError, api } from '../api/client.js';
import { useT } from '../i18n/index.js';
import { deriveBrowser, dayEyebrow, groupByDay } from './timeline/helpers.js';
import { SweepCard } from './timeline/SweepCard.js';
import { StatsRail } from './timeline/StatsRail.js';
import { TimelineFilters } from './timeline/TimelineFilters.js';
import type { FilterState } from './timeline/TimelineFilters.js';
import { PageDetail } from './detail/PageDetail.js';
import { useRestoreFlow, sessionFromSweep } from './restore/RestoreFlow.js';

const PAGE_SIZE = 20;

export function TimelinePage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { t, plural } = useT();

  const [filters, setFilters] = useState<FilterState>({ browser: 'all', days: 0, onlyContent: false });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [detail, setDetail] = useState<{ item: Item; sweep: Sweep } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Sweep | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const sweepsQ = useInfiniteQuery({
    queryKey: ['sweeps'],
    queryFn: ({ pageParam }) => api.vault.sweeps({ limit: PAGE_SIZE, before: pageParam }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.nextBefore ?? undefined,
  });

  const usageQ = useQuery({ queryKey: ['usage'], queryFn: () => api.account.usage() });
  const dupQ = useQuery({ queryKey: ['dedup', 'summary'], queryFn: () => api.dedup.live(), staleTime: 60_000 });

  const sweeps = useMemo<Sweep[]>(() => sweepsQ.data?.pages.flatMap((p) => p.sweeps) ?? [], [sweepsQ.data]);

  const browsers = useMemo<BrowserName[]>(
    () => Array.from(new Set(sweeps.map((s) => deriveBrowser(s.deviceId)))).sort(),
    [sweeps],
  );

  const filtered = useMemo(() => {
    const cutoff = filters.days > 0 ? Date.now() - filters.days * 86_400_000 : 0;
    return sweeps.filter((s) => {
      if (filters.onlyContent && s.kind === 'import') return false;
      if (filters.browser !== 'all' && deriveBrowser(s.deviceId) !== filters.browser) return false;
      if (cutoff && s.clientCreatedAt < cutoff) return false;
      return true;
    });
  }, [sweeps, filters]);

  const groups = useMemo(() => groupByDay(filtered), [filtered]);

  const dupCount = dupQ.data?.groups.reduce((n, g) => n + Math.max(0, g.members.length - 1), 0) ?? 0;
  const tabsSaved = usageQ.data?.counts.items ?? (sweeps.length ? sweeps.reduce((n, s) => n + s.tabCount, 0) : null);

  const delSweep = useMutation({
    mutationFn: (id: string) => api.vault.deleteSweep(id),
    onSuccess: (_res, id) => {
      void qc.invalidateQueries({ queryKey: ['sweeps'] });
      void qc.invalidateQueries({ queryKey: ['usage'] });
      void qc.invalidateQueries({ queryKey: ['dedup', 'summary'] });
      setExpanded((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setDetail((d) => (d && d.sweep.id === id ? null : d));
      setPendingDelete(null);
      setToast(t('timeline.toast.sessionDeleted'));
    },
  });

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const restore = useRestoreFlow();
  async function restoreSession(s: Sweep): Promise<void> {
    try {
      const { items } = await api.vault.sweep(s.id);
      restore.start(sessionFromSweep(s, items));
    } catch {
      setToast(t('timeline.toast.loadFailed'));
    }
  }

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const isNetworkError = sweepsQ.error != null && !(sweepsQ.error instanceof ApiError);

  return (
    <div className="tlp-layout">
      <style>{TLP_CSS}</style>

      {/* ── feed ── */}
      <div className="tlp-feed">
        <TimelineFilters value={filters} onChange={setFilters} browsers={browsers} />

        {sweepsQ.isPending && <FeedSkeleton />}

        {sweepsQ.isError && (
          <ErrorPanel
            offline={isNetworkError}
            onRetry={() => void sweepsQ.refetch()}
          />
        )}

        {!sweepsQ.isPending && !sweepsQ.isError && sweeps.length === 0 && (
          <FirstRunEmpty onImport={() => navigate('/import')} />
        )}

        {!sweepsQ.isPending && !sweepsQ.isError && sweeps.length > 0 && groups.length === 0 && (
          <div style={{ paddingTop: 8 }}>
            <EmptyState
              icon={<Icon name="filter" size={18} color="var(--text-3)" />}
              title={t('timeline.noMatch.title')}
              description={t('timeline.noMatch.body')}
              action={
                <Button variant="secondary" size="sm" onClick={() => setFilters({ browser: 'all', days: 0, onlyContent: false })}>
                  {t('common.clearFilters')}
                </Button>
              }
            />
          </div>
        )}

        {groups.map((g) => (
          <div key={g.key} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="tlp-dayhead">
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.14em', color: 'var(--accent-ink)' }}>
                — {dayEyebrow(g.ts)}
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-3)' }}>
                {plural('unit.sessions', g.sweeps.length).toUpperCase()} · {plural('unit.tabs', g.tabs).toUpperCase()}
              </span>
            </div>
            {g.sweeps.map((s) => (
              <SweepCard
                key={s.id}
                sweep={s}
                expanded={expanded.has(s.id)}
                onToggle={() => toggleExpand(s.id)}
                onOpenItem={(item) => setDetail({ item, sweep: s })}
                onRequestDelete={() => setPendingDelete(s)}
                onRestore={() => void restoreSession(s)}
              />
            ))}
          </div>
        ))}

        {sweepsQ.hasNextPage && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 0 8px' }}>
            <Button
              variant="ghost"
              size="sm"
              loading={sweepsQ.isFetchingNextPage}
              onClick={() => void sweepsQ.fetchNextPage()}
            >
              {t('timeline.loadOlder')}
            </Button>
          </div>
        )}
      </div>

      {/* ── right rail ── */}
      <div className="tlp-rail">
        <StatsRail
          tabsSaved={tabsSaved}
          dupCount={dupCount}
          loading={usageQ.isPending && tabsSaved === null}
          sweeps={sweeps}
          onReviewDupes={() => navigate('/duplicates')}
        />
      </div>

      {/* ── page detail slide-over ── */}
      {detail && <PageDetail item={detail.item} sweep={detail.sweep} onClose={() => setDetail(null)} />}

      {/* ── delete sweep confirm ── */}
      {pendingDelete && (
        <Modal
          title={t('timeline.delete.title')}
          subtitle={t('timeline.delete.subtitle', {
            title: pendingDelete.title ?? t('timeline.delete.fallbackTitle'),
            tabs: plural('unit.tabs', pendingDelete.tabCount),
          })}
          onClose={() => setPendingDelete(null)}
          width={420}
          footer={
            <>
              <Button variant="ghost" onClick={() => setPendingDelete(null)}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" loading={delSweep.isPending} onClick={() => delSweep.mutate(pendingDelete.id)}>
                {t('timeline.delete.confirm')}
              </Button>
            </>
          }
        >
          <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
            {t('timeline.delete.body')}
          </span>
        </Modal>
      )}

      {toast && <Toast tone="success" title={toast} />}

      {/* ── restore session confirm → open → undo ── */}
      {restore.node}
    </div>
  );
}

// ── feed states ─────────────────────────────────────────────────────────────

function FeedSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 4 }}>
      <Skeleton width={200} height={11} />
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ background: 'var(--surface-1)', border: '1px solid var(--line)', borderRadius: 'var(--r-lg)', padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <Skeleton width={64} height={22} radius={6} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
            <Skeleton width="40%" height={14} />
            <Skeleton width={160} height={10} />
          </div>
          <Skeleton width={90} height={30} radius="var(--r-pill)" />
        </div>
      ))}
    </div>
  );
}

function ErrorPanel({ offline, onRetry }: { offline: boolean; onRetry: () => void }) {
  const { t } = useT();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', textAlign: 'center', padding: '48px 24px', background: 'var(--surface-1)', border: '1px solid var(--line)', borderRadius: 'var(--r-lg)' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, borderRadius: 16, background: 'var(--amber-tint)', border: '1px solid var(--amber-line)' }}>
        <Icon name={offline ? 'moon' : 'clock'} size={22} color="var(--amber-ink)" />
      </span>
      <span style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 600 }}>
        {offline ? t('timeline.offline.title') : t('timeline.error.title')}
      </span>
      <span style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-2)', maxWidth: 360 }}>
        {offline ? t('timeline.offline.body') : t('timeline.error.body')}
      </span>
      <Button variant="secondary" onClick={onRetry}>
        {t('common.tryAgain')}
      </Button>
    </div>
  );
}

function FirstRunEmpty({ onImport }: { onImport: () => void }) {
  const { t } = useT();
  return (
    <div className="tlp-empty">
      <Logo size={56} />
      <span style={{ fontFamily: 'var(--font-display-cond)', fontSize: 26, fontWeight: 700, letterSpacing: '.02em', textTransform: 'uppercase' }}>
        {t('timeline.empty.title')}
      </span>
      <span style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-2)', maxWidth: 400 }}>
        {t('timeline.empty.body')}
      </span>
      <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Button variant="primary" onClick={onImport} icon={<Icon name="download" size={14} color="currentColor" />}>
          {t('timeline.empty.import')}
        </Button>
        <Button variant="ghost" onClick={onImport}>
          {t('timeline.empty.importOneTab')}
        </Button>
      </div>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--text-3)', marginTop: 2 }}>
        {t('timeline.empty.tip')}
      </span>
    </div>
  );
}

const TLP_CSS = `
.tlp-layout{display:flex;gap:24px;padding:24px 28px;align-items:flex-start}
.tlp-feed{flex:1;min-width:0;display:flex;flex-direction:column;gap:14px}
.tlp-rail{width:280px;flex-shrink:0;position:sticky;top:24px;align-self:flex-start;display:flex;flex-direction:column;gap:14px}
.tlp-filters{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.tlp-dayhead{display:flex;align-items:baseline;justify-content:space-between}
.tlp-empty{min-height:440px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;text-align:center;padding:40px}
@media (max-width:1080px){.tlp-rail{display:none}.tlp-layout{padding:20px 16px}}
@media (max-width:560px){.tlp-layout{padding:16px 12px}}
`;
